import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { Table } from 'legacy/lut/library/list'
import { FC, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { Static } from 'core-components/types'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { Crud } from 'core/types/Crud'
import { CreateNewQbr } from 'partner360/components/QBR/components/qbrDocumentDetails/components/createNewQbr/CreateNewQbr'
import { selectQBRRtm } from 'partner360/features/QBR'
import { IQBRDocumentsListItem } from 'partner360/models/qbr/IQBRDocumentsListItem'

import { IQBRDocumentsTableProps } from '../../../../models/qbrTableProps'
import { TRANSLATION_KEYS } from '../../../../translations'
import { QBRDocumentsFilterArea } from '../../../qbrDocumentsFilterArea/QBRDocumentsFilterArea'
import { getColumns } from '../../helpers/getColumns'
import { QbrCloneVersionDialog } from '../qbrCloneVersionDialog/QbrCloneVersionDialog'
import { QbrDeleteVersionDialog } from '../qbrDeleteVersionDialog/QbrDeleteVersionDialog'

import { useFetchQbrDocuments } from './hooks/useFetchQbrDocuments'

import styles from './QBRDocumentsTable.module.scss'

export const QBRDocumentsTable: FC<IQBRDocumentsTableProps> = ({
  metrics,
  partnerType,
  onCreateNewDocument,
  openQbrDocumentUploadDialog,
  openDocumentDetail,
}) => {
  const { startLoadData, stopLoadData } = useMeasureController(metrics)
  const user = useSelector(selectUser)
  const rtm = useSelector(selectQBRRtm)

  const [myQbrValue, setMyQbrValue] = useState(true)
  const [isOnlyLegacy, setIsOnlyLegacy] = useState(false)
  const [fYearValue, setFYearValue] = useState<string[]>([])
  const [geoValue, setGeoValue] = useState<string[]>([])
  const [creatorsValue, setCreatorsValue] = useState<string[]>([])
  const [partnersValue, setPartnersValue] = useState<string[]>([])
  const [selectedVersionToClone, setSelectedVersionToClone] = useState<IQBRDocumentsListItem>()
  const [itemToDelete, setItemToDelete] = useState<IQBRDocumentsListItem>()

  const { isSuperAdmin } = useUserRoles()
  const { data, setData, initialData, setInitialData, isLoading } = useFetchQbrDocuments({
    myQbrValue,
    partnerType,
  })

  const openDocument = useCallback(
    async (item: IQBRDocumentsListItem) => {
      openDocumentDetail(item.EntryId, item.IsLegacyDocument === 1)
    },
    [openDocumentDetail]
  )

  const columns = useMemo(
    () =>
      getColumns({
        isSuperAdmin,
        userEmail: user.email,
        setSelectedVersionToClone,
        setItemToDelete,
        openDocument,
      }),
    [openDocument, user.email, isSuperAdmin]
  )

  const source = useMemo(() => {
    return new Crud({
      fetcher: new Static(data),
      keyField: 'EntryId',
      defaultSort: { field: 'EntryId', dir: 'desc' },
    })
  }, [data])

  const onApplyFilter = useCallback(() => {
    if (
      !myQbrValue &&
      !fYearValue.length &&
      !geoValue.length &&
      !creatorsValue.length &&
      !partnersValue.length &&
      !isOnlyLegacy
    ) {
      setData(initialData)
      return
    }
    const filteredData = initialData.filter((el) => {
      const market = el.Market || ''
      const partnerIds = el.PartnerIDs || ([] as string[])
      const conditionCreatedBy = myQbrValue ? el.CreatedBy === user.id : true
      const conditionFYears = fYearValue.length ? fYearValue.includes(el.FYearQuarter || '') : true
      const conditionGeo = geoValue.length ? geoValue.find((geo) => market.indexOf(geo) >= 0) : true
      const conditionPartners = partnersValue.length
        ? partnersValue.find((partnerId) => partnerIds.toString().indexOf(partnerId) >= 0)
        : true
      const conditionCreators = creatorsValue.length ? creatorsValue.includes(el.CreatedBy) : true
      const conditionOnlyLegacy = isOnlyLegacy ? el.IsLegacyDocument === 1 : true
      return (
        conditionFYears &&
        conditionCreatedBy &&
        conditionCreators &&
        conditionPartners &&
        conditionGeo &&
        conditionOnlyLegacy
      )
    })
    setData(filteredData)
  }, [
    creatorsValue,
    fYearValue,
    geoValue,
    myQbrValue,
    partnersValue,
    user.id,
    initialData,
    isOnlyLegacy,
    setData,
  ])

  const onResetFilter = useCallback(() => {
    setMyQbrValue(false)
    setFYearValue([])
    setCreatorsValue([])
    setGeoValue([])
    setPartnersValue([])
    setIsOnlyLegacy(false)
    setData(initialData)
  }, [setData, initialData])

  const qbrCloneCloseDialog = useCallback(() => {
    setSelectedVersionToClone(undefined)
  }, [])

  const qbrDeleteVersionDialog = useCallback(() => {
    setItemToDelete(undefined)
  }, [])

  return (
    <div>
      <LoaderOverlay isVisible={isLoading} />
      <div className={styles.buttonsBlock}>
        <CreateNewQbr className={styles.qbrButton} rtm={rtm} onClick={onCreateNewDocument} />

        <Button className={styles.qbrButton} onClick={openQbrDocumentUploadDialog}>
          <Translation text={TRANSLATION_KEYS.buttons.uploadDocument} />
        </Button>
      </div>
      <QBRDocumentsFilterArea
        myQbrValue={myQbrValue}
        onChangeMyQbrFilter={setMyQbrValue}
        fyearValue={fYearValue}
        onChangeFyearValue={setFYearValue}
        qbrDocumentsData={data}
        geoValue={geoValue}
        onChangeGeoValue={setGeoValue}
        creatorsValue={creatorsValue}
        onChangeCreatorsValue={setCreatorsValue}
        partnersValue={partnersValue}
        onChangePartnersValue={setPartnersValue}
        isOnlyLegacy={isOnlyLegacy}
        onChangeIsOnlyLegacy={setIsOnlyLegacy}
        onApplyFilter={onApplyFilter}
        onResetFilter={onResetFilter}
      />
      <Table<IQBRDocumentsListItem>
        header
        columns={columns}
        hasTranslate
        source={source}
        measure={{ startLoadData, stopLoadData }}
        pagination
      />
      <QbrCloneVersionDialog
        selectedVersion={selectedVersionToClone || ({} as IQBRDocumentsListItem)}
        qbrDocumentsData={data}
        setQbrDocumentsData={setData}
        show={Boolean(selectedVersionToClone)}
        closeDialog={qbrCloneCloseDialog}
      />
      <QbrDeleteVersionDialog
        selectedVersion={itemToDelete || ({} as IQBRDocumentsListItem)}
        qbrDocumentsData={data}
        setQbrDocumentsData={setData}
        setInitialData={setInitialData}
        show={Boolean(itemToDelete)}
        closeDialog={qbrDeleteVersionDialog}
      />
    </div>
  )
}
